import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import DashboardLayout from '@/components/layout/DashboardLayout';

export default function AffiliateDetail() {
  const { id } = useParams(); // user id for affiliate
  const navigate = useNavigate();

  const { data: code } = useQuery({
    queryKey: ['admin-affiliate-code', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await supabase
        .from('affiliate_codes')
        .select('*')
        .eq('user_id', id)
        .maybeSingle();
      return data as any;
    },
    enabled: !!id,
  });

  const { data: referrals } = useQuery({
    queryKey: ['admin-affiliate-referrals', id],
    queryFn: async () => {
      if (!id) return [];
      const { data } = await supabase
        .from('referrals')
        .select('id, referred_user_id, status, transaction_type, transaction_amount, created_at')
        .eq('affiliate_user_id', id)
        .order('created_at', { ascending: false })
        .limit(100);
      return (data || []) as any[];
    },
    enabled: !!id,
  });

  return (
    <DashboardLayout title="Affiliate" maxWidth="6xl" headerActions={<Button variant="outline" onClick={() => navigate('/admin/business/affiliates')}>Back</Button>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 border rounded-lg md:col-span-1">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Code</div>
              <div className="font-medium">{code?.custom_code || '—'}</div>
            </div>
            <div className="space-y-1 mt-4">
              <div className="text-sm text-muted-foreground">Active</div>
              <div className="font-medium">{code?.is_active ? 'Yes' : 'No'}</div>
            </div>
          </div>
          <div className="p-4 border rounded-lg md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Referrals</h2>
            </div>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Referred User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Transaction</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(referrals || []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.id}</TableCell>
                      <TableCell className="font-mono text-xs">{r.referred_user_id}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell className="text-xs">{r.transaction_type || '—'}</TableCell>
                      <TableCell>{r.transaction_amount ? `$${Number(r.transaction_amount).toFixed(2)}` : '—'}</TableCell>
                      <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {(referrals || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">No referrals</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
    </DashboardLayout>
  );
}


